/**
 * TemplatesPage — 邮件模板管理（docs/ui_page_head_detail 主从布局）
 * 左栏：收件域选择器（DomainSidebar），模板按收件域归属管理
 * 右栏：选中收件域下的模板列表（PagedGrid）+ 新建/复制入口
 * 复制：弹出目标域多选对话框 → 若目标域存在同名模板，二次确认「覆盖 / 跳过」
 */
import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Card,
  Textarea,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  InfoLabel,
  Switch,
  Combobox,
  Option,
  Divider,
  Checkbox,
  Select,
  Badge,
  mergeClasses,
} from '@fluentui/react-components'
import {
  AddRegular,
  SearchRegular,
  DismissRegular,
  EditRegular,
  SparkleRegular,
  GlobeRegular,
  CopyArrowRightRegular,
  DeleteRegular,
  PlayRegular,
  PauseRegular,
  SendRegular,
  CheckmarkRegular,
  DismissCircleRegular,
} from '@fluentui/react-icons'
import { templatesApi } from '@/api/templates'
import { domainsApi } from '@/api/domains'
import { llmApi } from '@/api/llm'
import type { Template, TemplateVariable, Domain } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { DetailGrid, DetailField } from '@/components/DetailField'
import VariablesEditor, { cleanVariables } from '@/components/VariablesEditor'
import { formatDate } from '@/lib/format'
import DomainSidebar from '@/components/DomainSidebar'

const PAGE_SIZE = 10

/** AI 生成「需求描述」的预设场景，点击即填入输入框 */
const AI_PRESETS: { key: string; label: string; prompt: string }[] = [
  { key: 'welcome', label: '注册欢迎邮件', prompt: '生成一封面向新注册用户的欢迎邮件，语气亲切热情，介绍产品核心价值，并使用占位符 {{name}} 表示用户名。' },
  { key: 'verify', label: '验证码邮件', prompt: '生成一封验证码电子邮件，主题简洁，正文突出安全提醒：验证码 {{code}} 仅本人使用、有效期 10 分钟，切勿泄露给他人。' },
  { key: 'reset', label: '密码重置邮件', prompt: '生成一封密码重置邮件，说明用户请求了重置密码并提供重置链接，提示若非本人操作请忽略并及时修改账号安全设置。' },
  { key: 'order', label: '订单确认邮件', prompt: '生成一封订单确认邮件，确认订单号 {{orderNo}} 已成功提交，列出订单金额与预计送达时间，语气专业友好。' },
  { key: 'shipping', label: '发货通知邮件', prompt: '生成一封物流发货通知邮件，告知订单 {{orderNo}} 已发货，提供运单号 {{trackingNo}} 和物流查询方式。' },
  { key: 'invoice', label: '账单 / 发票邮件', prompt: '生成一封账单邮件，列出本期消费明细、金额 {{amount}} 和应还款日期，提示如有疑问可随时联系客服。' },
  { key: 'promo', label: '限时促销邮件', prompt: '生成一封限时促销活动邮件，突出折扣力度和优惠码 {{code}}，营造适度的紧迫感，引导用户尽快下单。' },
  { key: 'expire', label: '会员到期提醒', prompt: '生成一封会员即将到期提醒邮件，告知到期日期 {{expireDate}}，说明续费权益与续费链接，语气诚恳、避免施压。' },
  { key: 'security', label: '安全告警邮件', prompt: '生成一封安全告警邮件，提示账号 {{name}} 于 {{time}} 检测到新设备登录，若非本人操作请立即修改密码并开启二次验证。' },
  { key: 'report', label: '定期数据报告', prompt: '生成一封周期性数据报告邮件，汇总本期关键指标 {{metrics}}，用简洁排版呈现，并附数据下载链接。' },
  { key: 'survey', label: '满意度调查邮件', prompt: '生成一封客户满意度调查邀请邮件，说明调查目的、预计耗时约 3 分钟并提供调查链接，结尾表达感谢。' },
  { key: 'release', label: '新功能上线通知', prompt: '生成一封新功能上线通知邮件，介绍本次新增的 {{feature}} 功能亮点、使用方法和上手建议，语气积极。' },
]

const useStyles = makeStyles({
  root: { padding: '8px 12px', height: '100%', boxSizing: 'border-box' },
  inner: { height: '100%', display: 'flex', flexDirection: 'column' },
  split: { display: 'flex', gap: '16px', alignItems: 'stretch', flex: 1, minHeight: 0, overflow: 'hidden', padding: '2px 0 2px 2px' },
  right: { flex: '1 1 auto', minWidth: 0, height: '100%', boxSizing: 'border-box' },
  rightRoot: { display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%', overflow: 'hidden', boxSizing: 'border-box' },
  rightHeader: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', flexShrink: 0 },
  headerTitle: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 },
  headerName: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 },
  headerSub: { fontFamily: 'var(--font-monospace, monospace)', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rightPlaceholder: { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', color: tokens.colorNeutralForeground3, padding: '40px' },
  queryCard: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', flexShrink: 0 },
  formRow: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' },
  keywordField: { flex: '2 0 240px', minWidth: '220px', flexShrink: 0 },
  filterRow: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  actionsRow: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
  tableCard: { padding: '16px', flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' },
  tableWrap: { flex: 1, minHeight: 0, overflow: 'hidden' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 16px' },
  formGridItem: { minWidth: 0 },
  colSpan2: { gridColumn: '1 / -1' },
  dialogSurface: { width: '65vw', minWidth: '600px', maxWidth: '1000px' },
  aiPanel: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px', marginTop: '8px', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground2 },
  aiPanelTitle: { display: 'flex', alignItems: 'center', gap: '6px', fontWeight: tokens.fontWeightSemibold },
  aiActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
  presetRow: { display: 'flex', gap: '12px', alignItems: 'flex-start', gridColumn: '1 / -1' },
  presetPrompt: { flex: '1', minWidth: '0' },
  presetWrap: { width: '240px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '4px' },
  presetCol: { maxHeight: '140px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px', borderLeft: `1px solid ${tokens.colorNeutralStroke2}`, paddingLeft: '12px' },
  presetHeading: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  presetItem: { justifyContent: 'flex-start', textAlign: 'left' },
  copyList: { display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '320px', overflowY: 'auto' },
  copyItem: { display: 'flex', gap: '8px', alignItems: 'center', padding: '6px 8px', borderRadius: tokens.borderRadiusMedium },
  copyItemDisabled: { opacity: 0.5 },
  conflictList: { display: 'flex', flexDirection: 'column', gap: '8px', margin: '8px 0' },
  conflictRow: { display: 'flex', gap: '10px', alignItems: 'center', padding: '8px 10px', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground2 },
  conflictText: { flex: 1, minWidth: 0 },
  conflictName: { display: 'flex', alignItems: 'center', gap: '6px', fontWeight: tokens.fontWeightSemibold },
  conflictSub: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  conflictEmpty: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, padding: '4px 2px' },
})

interface DomainFormState {
  name: string
  description: string
  max_quota_bytes: string
  max_users: string
}

const EMPTY_DOMAIN_FORM: DomainFormState = { name: '', description: '', max_quota_bytes: '', max_users: '' }

/** 审核状态 → 展示文案/颜色 */
const STATUS_META: Record<string, { label: string; color: 'subtle' | 'brand' | 'success' | 'warning' | 'danger' }> = {
  draft: { label: '草稿', color: 'subtle' },
  pending: { label: '待审核', color: 'warning' },
  approved: { label: '已通过', color: 'success' },
  rejected: { label: '已驳回', color: 'danger' },
}

export default function TemplatesPage() {
  const styles = useStyles()
  const queryClient = useQueryClient()

  // ── 收件域 ──
  const { data: domains = [], isLoading: domainsLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => domainsApi.list(),
  })
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const selectedDomain: Domain | undefined = selectedId != null ? domains.find((d) => d.id === selectedId) : undefined

  // 模板列表（按选中收件域过滤；未选中域时展示全部，含未归属模板）
  const { data: templates, isLoading } = useQuery({
    queryKey: ['templates', selectedId],
    queryFn: () => templatesApi.list(selectedId),
  })

  // 查询草稿态
  const [draftQ, setDraftQ] = useState('')
  const [query, setQuery] = useState('')

  // 分页
  const [page, setPage] = useState(1)

  // ── 新建 / 编辑模板 ──
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<{
    name: string
    subject: string
    body_html: string
    body_text: string
    variables: TemplateVariable[]
  }>({ name: '', subject: '', body_html: '', body_text: '', variables: [] })
  const [detailTemplate, setDetailTemplate] = useState<Template | null>(null)

  // AI 生成
  const [aiOpen, setAiOpen] = useState(false)
  const [aiProviderId, setAiProviderId] = useState<number | undefined>(undefined)
  const [aiModel, setAiModel] = useState<string | undefined>(undefined)
  const [aiPrompt, setAiPrompt] = useState('')
  const { data: aiProviders = [] } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: llmApi.list,
  })
  const enabledProviders = aiProviders.filter((p) => p.enabled)
  const currentProvider = enabledProviders.find((p) => p.id === aiProviderId) ?? enabledProviders[0]
  const aiGenerateMutation = useMutation({
    mutationFn: () =>
      llmApi.generate({
        provider_id: currentProvider?.id,
        model: aiModel || currentProvider?.default_model || undefined,
        prompt: aiPrompt,
        context: {
          name: form.name,
          subject: form.subject,
          body_html: form.body_html,
          body_text: form.body_text,
          variables: cleanVariables(form.variables),
        },
      }),
    onSuccess: (res) => {
      setForm((f) => ({
        ...f,
        subject: res.subject || f.subject,
        body_html: res.body_html || f.body_html,
        body_text: res.body_text || f.body_text,
      }))
      toast.success('AI 生成成功', '已将生成内容填入表单，可继续编辑')
      setAiOpen(false)
      setAiPrompt('')
    },
    onError: (err) => toast.error('AI 生成失败', err instanceof ApiError ? err.message : '请先在「设置 → AI 服务商」中配置服务商'),
  })

  // 多选
  const [selectionMode, setSelectionMode] = useState<'single' | 'multiselect'>('single')
  const [selectedRows, setSelectedRows] = useState<Template[]>([])

  const deleteMutation = useMutation({
    mutationFn: async (rows: Template[]) => {
      await Promise.all(rows.map((t) => templatesApi.remove(t.id)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      toast.success('删除成功', `已删除 ${selectedRows.length} 项`)
    },
    onError: (err) => {
      toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试')
    },
  })

  // 启用/停用模板（active 开关，与审核状态正交）
  const setActiveMutation = useMutation({
    mutationFn: (t: Template) => templatesApi.setActive(t.id, !t.active),
    onSuccess: (_data, t) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      // 同步刷新当前打开的详情弹窗，无需关闭重开
      setDetailTemplate((prev) => (prev && prev.id === t.id ? { ...prev, active: !t.active } : prev))
      toast.success(t.active ? '已停用' : '已启用', `模板「${t.name}」${t.active ? '已停用，发信将拒绝' : '已启用，可参与发信'}`)
    },
    onError: (err) => toast.error('操作失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // 模板审核（draft → pending → approved / rejected）
  const reviewMutation = useMutation({
    mutationFn: ({ t, action }: { t: Template; action: 'submit' | 'approve' | 'reject' }) =>
      templatesApi.review(t.id, action),
    onSuccess: (_data, { t, action }) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      // 同步刷新当前打开的详情弹窗
      const nextStatus = action === 'submit' ? 'pending' : action === 'approve' ? 'approved' : 'rejected'
      setDetailTemplate((prev) => (prev && prev.id === t.id ? { ...prev, status: nextStatus } : prev))
      const label = action === 'submit' ? '已提交审核' : action === 'approve' ? '已审核通过' : '已驳回'
      toast.success(label, `模板「${t.name}」${label === '已驳回' ? '已驳回，可修改后重新提交' : ''}`)
    },
    onError: (err) => toast.error('操作失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // 根据审核状态渲染审核操作按钮（draft/rejected→提交审核；pending→通过/驳回；approved→无）
  const renderReviewActions = (t: Template, compact?: boolean) => {
    if (t.status === 'pending') {
      return (
        <>
          <Button
            appearance="subtle"
            size={compact ? 'small' : 'small'}
            icon={<CheckmarkRegular />}
            title="审核通过该模板"
            onClick={(e) => { e?.stopPropagation?.(); reviewMutation.mutate({ t, action: 'approve' }) }}
            disabled={reviewMutation.isPending}
          >
            通过
          </Button>
          <Button
            appearance="subtle"
            size={compact ? 'small' : 'small'}
            icon={<DismissCircleRegular />}
            title="驳回该模板"
            onClick={(e) => { e?.stopPropagation?.(); reviewMutation.mutate({ t, action: 'reject' }) }}
            disabled={reviewMutation.isPending}
          >
            驳回
          </Button>
        </>
      )
    }
    if (t.status === 'draft' || t.status === 'rejected') {
      return (
        <Button
          appearance="subtle"
          size={compact ? 'small' : 'small'}
          icon={<SendRegular />}
          title="提交待审核"
          onClick={(e) => { e?.stopPropagation?.(); reviewMutation.mutate({ t, action: 'submit' }) }}
          disabled={reviewMutation.isPending}
        >
          提交审核
        </Button>
      )
    }
    return null
  }

  const openCreate = () => {
    setEditingId(null)
    setForm({ name: '', subject: '', body_html: '', body_text: '', variables: [] })
    setAiOpen(false)
    setAiPrompt('')
    setEditorOpen(true)
  }

  const openEdit = async (t: Template) => {
    setEditingId(t.id)
    setDetailTemplate(null)
    setAiOpen(false)
    setAiPrompt('')
    // 列表接口不含 body_html/body_text，需拉取完整详情后再填充表单
    const fill = (src: Template) => {
      let vars: TemplateVariable[] = []
      try {
        const parsed = typeof src.variables === 'string' ? JSON.parse(src.variables) : src.variables
        if (Array.isArray(parsed)) {
          vars = parsed
            .map((v) => ({
              name: typeof v?.name === 'string' ? v.name : '',
              desc: typeof v?.desc === 'string' ? v.desc : '',
            }))
            .filter((v) => v.name)
        }
      } catch { vars = [] }
      setForm({
        name: src.name,
        subject: src.subject ?? '',
        body_html: src.body_html ?? '',
        body_text: src.body_text ?? '',
        variables: vars,
      })
    }
    setEditorOpen(true)
    try {
      fill(await templatesApi.get(t.id))
    } catch {
      fill(t)
    }
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        subject: form.subject.trim(),
        body_html: form.body_html || null,
        body_text: form.body_text || null,
        variables: cleanVariables(form.variables),
        domain_id: editingId === null ? selectedId : undefined,
      }
      if (editingId === null) {
        return templatesApi.create(payload)
      }
      return templatesApi.update(editingId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      toast.success(editingId === null ? '模板已创建' : '模板已更新')
      setEditorOpen(false)
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '保存失败'),
  })

  // ── 复制模板 ──
  const [copyTarget, setCopyTarget] = useState<Template | null>(null)
  const [copyChecked, setCopyChecked] = useState<number[]>([])
  const [conflictAction, setConflictAction] = useState<'overwrite' | 'skip'>('overwrite')
  const [conflictDomains, setConflictDomains] = useState<number[]>([])

  const copyMutation = useMutation({
    mutationFn: async () => {
      if (!copyTarget) return
      await templatesApi.copy(copyTarget.id, {
        target_domain_ids: copyChecked,
        overwrite: conflictAction === 'overwrite',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      setCopyTarget(null)
      setCopyChecked([])
      setConflictDomains([])
      toast.success('复制成功', `已复制到 ${copyChecked.length} 个收件域`)
    },
    onError: (err) => toast.error('复制失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  const openCopy = (t: Template) => {
    setCopyTarget(t)
    setCopyChecked([])
    setConflictDomains([])
    setConflictAction('overwrite')
  }

  // 目标域中已存在同名模板的集合（用于二次确认）
  const conflictDomainSet = useMemo(() => {
    if (!copyTarget) return new Set<number>()
    const name = copyTarget.name.trim().toLowerCase()
    const set = new Set<number>()
    for (const t of templates ?? []) {
      if (t.domain_id != null && t.name.trim().toLowerCase() === name) set.add(t.domain_id)
    }
    return set
  }, [copyTarget, templates])

  const copyCheckbox = (d: Domain) => ({
    checked: copyChecked.includes(d.id),
    onChange: () => {
      setCopyChecked((prev) =>
        prev.includes(d.id) ? prev.filter((x) => x !== d.id) : [...prev, d.id]
      )
    },
  })

  // ── 收件域增删改 ──
  const [createOpen, setCreateOpen] = useState(false)
  const [domainForm, setDomainForm] = useState<DomainFormState>(EMPTY_DOMAIN_FORM)
  const [editTarget, setEditTarget] = useState<Domain | null>(null)
  const [editForm, setEditForm] = useState<DomainFormState>(EMPTY_DOMAIN_FORM)
  const [deleteTarget, setDeleteTarget] = useState<Domain | null>(null)

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string; max_quota_bytes?: number; max_users?: number }) => domainsApi.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      setCreateOpen(false)
      setSelectedId(res.id)
      toast.success('创建成功', '收件域已创建')
    },
    onError: (err) => toast.error('创建失败', err instanceof ApiError ? err.message : '创建失败，请稍后重试'),
  })
  const handleCreate = () => {
    const name = domainForm.name.trim()
    if (!name) { toast.error('请填写域名'); return }
    createMutation.mutate({
      name,
      description: domainForm.description.trim() || undefined,
      max_quota_bytes: domainForm.max_quota_bytes.trim() ? Number(domainForm.max_quota_bytes) : undefined,
      max_users: domainForm.max_users.trim() ? Number(domainForm.max_users) : undefined,
    })
  }

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editTarget) return
      await domainsApi.update(editTarget.id, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        max_quota_bytes: editForm.max_quota_bytes.trim() ? Number(editForm.max_quota_bytes) : null,
        max_users: editForm.max_users.trim() ? Number(editForm.max_users) : null,
        default_forward_email: editTarget.defaultForwardEmail,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      setEditTarget(null)
      toast.success('保存成功', '收件域信息已更新')
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  const deleteDomainMutation = useMutation({
    mutationFn: async (d: Domain) => domainsApi.delete(d.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      setDeleteTarget(null)
      if (selectedId === deleteTarget?.id) setSelectedId(null)
      toast.success('删除成功', '收件域已删除')
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  const refreshDomains = () => {
    queryClient.invalidateQueries({ queryKey: ['domains'] })
    queryClient.invalidateQueries({ queryKey: ['templates'] })
  }

  // 前端过滤
  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    const list = templates ?? []
    if (!q) return list
    return list.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.subject ?? '').toLowerCase().includes(q),
    )
  }, [templates, q])

  const hasActiveFilter = !!q
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const resetQuery = () => { setDraftQ(''); setQuery(''); setPage(1) }
  const applyQuery = () => { setQuery(draftQ); setPage(1) }

  const columns: PagedColumn<Template>[] = [
    {
      key: 'name',
      header: '模板名称',
      headerTooltip: '邮件模板的名称，用于识别用途',
      width: '200px',
      render: (t) => <Text weight="semibold">{t.name}</Text>,
    },
    {
      key: 'subject',
      header: '主题',
      width: '260px',
      render: (t) => t.subject ?? '-',
    },
    {
      key: 'status',
      header: '状态',
      width: '120px',
      render: (t) => {
        const meta = STATUS_META[t.status] ?? { label: t.status ?? '-', color: 'subtle' as const }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Badge appearance="tint" color={meta.color}>{meta.label}</Badge>
            {!t.active && <Badge appearance="tint" color="danger">已停用</Badge>}
          </div>
        )
      },
    },
    {
      key: 'updated_at',
      header: '更新时间',
      width: '160px',
      render: (t) => formatDate(t.updated_at),
    },
    {
      key: 'actions',
      header: '操作',
      width: '170px',
      render: (t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          {renderReviewActions(t, true)}
          <Button
            appearance="subtle"
            size="small"
            icon={t.active ? <PauseRegular /> : <PlayRegular />}
            title={t.active ? '停用模板（发信将拒绝）' : '启用模板'}
            onClick={(e) => {
              e.stopPropagation()
              setActiveMutation.mutate(t)
            }}
            disabled={setActiveMutation.isPending}
          >
            {t.active ? '停用' : '启用'}
          </Button>
        </div>
      ),
    },
  ]

  const countLabel = (d: Domain) => {
    const n = templates?.filter((t) => t.domain_id === d.id).length ?? 0
    return `${n} 个模板`
  }

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.split}>
          {/* ── 左栏：收件域选择器 ── */}
          <DomainSidebar
            domains={domains}
            loading={domainsLoading}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onNew={() => setCreateOpen(true)}
            onEdit={setEditTarget}
            onDelete={setDeleteTarget}
            onRefresh={refreshDomains}
            countLabel={countLabel}
            emptyHint="暂无收件域，点击左上角「+」新建，模板将按收件域归属管理"
          />

          {/* ── 右栏：模板列表 ── */}
          <div className={styles.right}>
            {selectedDomain ? (
              <div className={styles.rightRoot}>
                <div className={styles.rightHeader}>
                  <div className={styles.headerTitle}>
                    <span className={styles.headerName}>
                      <GlobeRegular />
                      {selectedDomain.name}
                      <span style={{ fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3, fontWeight: tokens.fontWeightRegular }}>
                        ({filtered.length} 个模板)
                      </span>
                    </span>
                    <span className={styles.headerSub}>
                      {selectedDomain.description || '收件域'} · 该收件域使用的邮件模板
                    </span>
                  </div>
                </div>

                {/* 查询面板 */}
                <Card className={styles.queryCard}>
                  <div className={styles.formRow}>
                    <Field
                      className={styles.keywordField}
                      label={<InfoLabel info="按模板名称或主题模糊搜索，留空显示全部。">关键词</InfoLabel>}
                    >
                      <Input
                        contentBefore={<SearchRegular />}
                        contentAfter={draftQ ? (
                          <Button appearance="transparent" size="small" icon={<DismissRegular />} aria-label="清空" onClick={() => setDraftQ('')} />
                        ) : undefined}
                        placeholder="搜索模板名称 / 主题…"
                        value={draftQ}
                        onChange={(_, d) => setDraftQ(d.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') applyQuery() }}
                      />
                    </Field>
                  </div>
                  {hasActiveFilter && (
                    <div className={styles.filterRow}>已应用过滤：关键词 “{query}”</div>
                  )}
                  <div className={styles.actionsRow}>
                    <Button appearance="subtle" onClick={resetQuery}>重置</Button>
                    <Button appearance="secondary" onClick={applyQuery}>查询</Button>
                    <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>新建模板</Button>
                  </div>
                </Card>

                {/* 数据表 */}
                <Card className={styles.tableCard}>
                  <div className={styles.tableWrap}>
                    <PagedGrid<Template>
                      rows={pageItems}
                      columns={columns}
                      loading={isLoading}
                      rowKey={(t) => String(t.id)}
                      total={filtered.length}
                      page={safePage}
                      pageSize={PAGE_SIZE}
                      onPageChange={setPage}
                      emptyText="该收件域暂无邮件模板"
                      toolbarLeft={
                        <>
                          <Switch
                            checked={selectionMode === 'multiselect'}
                            onChange={(_, d) => setSelectionMode(d.checked ? 'multiselect' : 'single')}
                            label="多选"
                          />
                          {selectionMode === 'single' && selectedRows.length === 1 && (
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<CopyArrowRightRegular />}
                              onClick={() => openCopy(selectedRows[0])}
                            >
                              复制模板
                            </Button>
                          )}
                        </>
                      }
                      selectionMode={selectionMode}
                      onSelectionChange={setSelectedRows}
                      onDelete={(rows) => deleteMutation.mutateAsync(rows)}
                      onRowClick={(t) => setDetailTemplate(t)}
                    />
                  </div>
                </Card>
              </div>
            ) : (
              <div className={styles.rightPlaceholder}>
                <GlobeRegular style={{ fontSize: '40px' }} />
                <Text>从左侧选择一个收件域，管理该收件域使用的邮件模板</Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 新建/编辑模板 Dialog */}
      <Dialog open={editorOpen} onOpenChange={(_, d) => { if (!d.open) setEditorOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title={editingId === null ? '新建模板' : '编辑模板'} onClose={() => setEditorOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="邮件模板的名称，用于识别用途。">模板名称</InfoLabel>}
                  required
                >
                  <Input value={form.name} onChange={(_, d) => setForm((f) => ({ ...f, name: d.value }))} placeholder="例如：验证码邮件、订单通知" />
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="邮件主题，支持 {{变量}} 占位符。">主题</InfoLabel>}
                  required
                >
                  <Input value={form.subject} onChange={(_, d) => setForm((f) => ({ ...f, subject: d.value }))} placeholder="例如：您的验证码是 {{code}}" />
                </Field>
                <Field
                  className={`${styles.formGridItem} ${styles.colSpan2}`}
                  label={<InfoLabel info="邮件的 HTML 正文，支持 {{变量}} 占位符。">HTML 正文</InfoLabel>}
                >
                  <Textarea rows={8} value={form.body_html} onChange={(_, d) => setForm((f) => ({ ...f, body_html: d.value }))} placeholder="<p>您好 {{name}}，您的验证码是 {{code}}</p>" style={{ fontFamily: 'monospace' }} />
                </Field>
                <Field
                  className={`${styles.formGridItem} ${styles.colSpan2}`}
                  label={<InfoLabel info="邮件的纯文本正文，作为无 HTML 客户端的备选。">纯文本正文</InfoLabel>}
                >
                  <Textarea rows={3} value={form.body_text} onChange={(_, d) => setForm((f) => ({ ...f, body_text: d.value }))} placeholder="您好 {{name}}，您的验证码是 {{code}}" />
                </Field>
                <Field
                  className={`${styles.formGridItem} ${styles.colSpan2}`}
                  label={<InfoLabel info="模板可用的变量定义。添加后可点击占位符复制，正文中使用 {{变量名}} 引用。">变量定义</InfoLabel>}
                >
                  <VariablesEditor
                    items={form.variables}
                    onChange={(vars) => setForm((f) => ({ ...f, variables: vars }))}
                  />
                </Field>
              </div>

              {aiOpen && (
                <div className={styles.aiPanel}>
                  <Divider style={{ marginBottom: '4px' }} />
                  <div className={styles.aiPanelTitle}>
                    <SparkleRegular />
                    <Text>AI 生成邮件内容</Text>
                  </div>
                  <div className={styles.formGrid}>
                    <Field className={styles.formGridItem} label={<InfoLabel info="选择使用哪个 AI 服务商生成。">AI 服务商</InfoLabel>}>
                      <Combobox
                        value={currentProvider?.name ?? '（无可用服务商）'}
                        selectedOptions={currentProvider ? [String(currentProvider.id)] : []}
                        onOptionSelect={(_, d) => setAiProviderId(Number(d.optionValue))}
                        disabled={enabledProviders.length === 0}
                      >
                        {enabledProviders.map((p) => (
                          <Option key={p.id} value={String(p.id)}>{p.name}</Option>
                        ))}
                      </Combobox>
                    </Field>
                    <Field className={styles.formGridItem} label={<InfoLabel info="使用的模型，留空使用服务商默认模型。">模型</InfoLabel>}>
                      <Combobox
                        value={aiModel ?? currentProvider?.default_model ?? ''}
                        selectedOptions={aiModel ? [aiModel] : []}
                        onOptionSelect={(_, d) => setAiModel(d.optionValue ?? '')}
                        freeform
                        placeholder="留空使用默认模型"
                        disabled={enabledProviders.length === 0}
                      >
                        {(currentProvider?.models ?? []).map((m) => (
                          <Option key={m} value={m}>{m}</Option>
                        ))}
                      </Combobox>
                    </Field>
                    <div className={styles.presetRow}>
                      <Field
                        className={styles.presetPrompt}
                        label={<InfoLabel info="描述你想要的邮件内容，例如：面向新用户的注册欢迎邮件，语气亲切。系统会自动约束使用 {{变量}} 占位符并返回主题 + HTML 正文 + 纯文本正文。">需求描述</InfoLabel>}
                        required
                      >
                        <Textarea
                          rows={5}
                          value={aiPrompt}
                          onChange={(_, d) => setAiPrompt(d.value)}
                          placeholder="例如：生成一封注册欢迎邮件，包含验证码变量 {{code}}，突出安全提醒。"
                        />
                      </Field>
                      <div className={styles.presetWrap}>
                        <Text size={200} weight="semibold" className={styles.presetHeading}>预设场景（点击填入）</Text>
                        <div className={styles.presetCol}>
                          {AI_PRESETS.map((p) => (
                            <Button
                              key={p.key}
                              size="small"
                              appearance="transparent"
                              className={styles.presetItem}
                              title={p.prompt}
                              onClick={() => setAiPrompt(p.prompt)}
                            >
                              {p.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.aiActions}>
                    <Button appearance="secondary" onClick={() => setAiOpen(false)}>收起</Button>
                    <Button
                      appearance="primary"
                      icon={<SparkleRegular />}
                      disabled={aiGenerateMutation.isPending || !aiPrompt.trim() || enabledProviders.length === 0}
                      onClick={() => aiGenerateMutation.mutate()}
                    >
                      {aiGenerateMutation.isPending ? '生成中…' : '生成'}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="outline"
                icon={<SparkleRegular />}
                disabled={aiProviders.length === 0}
                title={aiProviders.length === 0 ? '请先在「设置 → AI 服务商」中配置服务商' : '使用 AI 生成邮件内容'}
                onClick={() => setAiOpen((v) => !v)}
              >
                {aiOpen ? '收起 AI 生成' : 'AI 生成'}
              </Button>
              <Button
                appearance="primary"
                disabled={saveMutation.isPending || !form.name.trim() || !form.subject.trim()}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isPending ? '保存中…' : '保存'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 模板详情 Dialog */}
      <Dialog open={detailTemplate !== null} onOpenChange={(_, d) => !d.open && setDetailTemplate(null)} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="模板详情" onClose={() => setDetailTemplate(null)} />
            <DialogContent>
              {detailTemplate && (
                <DetailGrid key={detailTemplate.id}>
                  <DetailField label="ID" value={detailTemplate.id} mono />
                  <DetailField label="模板名称" value={detailTemplate.name} help="邮件模板的名称，用于识别用途。" />
                  <DetailField label="主题" value={detailTemplate.subject || '无'} mono help="邮件主题，支持 {{变量}} 占位符。" />
                  <DetailField label="审核状态" value={STATUS_META[detailTemplate.status]?.label ?? detailTemplate.status ?? '-'} help="模板需审核通过后才能用于发信。" />
                  <DetailField label="启用状态" value={detailTemplate.active ? '已启用' : '已停用'} help="停用后即使审核通过也不能发信，需重新启用。" />
                  <DetailField label="HTML 正文" value={detailTemplate.body_html ?? '无'} mono colSpan help="邮件的 HTML 正文，支持 {{变量}} 占位符。" />
                  <DetailField label="纯文本正文" value={detailTemplate.body_text ?? '无'} mono colSpan help="邮件的纯文本正文，作为无 HTML 客户端的备选。" />
                  <DetailField label="变量定义" value={typeof detailTemplate.variables === 'string' ? detailTemplate.variables : JSON.stringify(detailTemplate.variables) || '无'} mono colSpan help="模板可用的变量定义列表。" />
                  <DetailField label="更新时间" value={detailTemplate.updated_at} />
                </DetailGrid>
              )}
            </DialogContent>
            <DialogActions>
              {detailTemplate && renderReviewActions(detailTemplate)}
              <Button
                appearance="outline"
                icon={detailTemplate?.active ? <PauseRegular /> : <PlayRegular />}
                onClick={() => detailTemplate && setActiveMutation.mutate(detailTemplate)}
                disabled={setActiveMutation.isPending}
              >
                {detailTemplate?.active ? '停用' : '启用'}
              </Button>
              <Button
                appearance="subtle"
                icon={<CopyArrowRightRegular />}
                onClick={() => detailTemplate && openCopy(detailTemplate)}
              >
                复制
              </Button>
              <Button
                appearance="primary"
                icon={<EditRegular />}
                onClick={() => detailTemplate && openEdit(detailTemplate)}
              >
                编辑
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 复制模板：目标域多选 Dialog */}
      <Dialog open={copyTarget !== null} onOpenChange={(_, d) => { if (!d.open) { setCopyTarget(null); setCopyChecked([]); setConflictDomains([]) } }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="复制模板" onClose={() => { setCopyTarget(null); setCopyChecked([]); setConflictDomains([]) }} />
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Text>
                将模板 <b>{copyTarget?.name}</b> 复制到以下收件域（可多选，当前域 {selectedDomain?.name} 已排除）：
              </Text>
              <div className={styles.copyList}>
                {domains
                  .filter((d) => d.id !== selectedId)
                  .map((d) => {
                    const conflict = conflictDomainSet.has(d.id)
                    return (
                      <div
                        key={d.id}
                        className={mergeClasses(styles.copyItem, conflict && styles.copyItemDisabled)}
                        title={conflict ? `目标域已存在同名模板「${copyTarget?.name}」，确认后需二次选择覆盖或跳过` : undefined}
                      >
                        <Checkbox {...copyCheckbox(d)} label={d.name} disabled={conflict} />
                        {conflict && <Text size={200} style={{ color: tokens.colorStatusWarningForeground1 }}>存在同名模板</Text>}
                      </div>
                    )
                  })}
                {domains.filter((d) => d.id !== selectedId).length === 0 && (
                  <div className={styles.conflictEmpty}>没有其它可选收件域</div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="subtle" onClick={() => { setCopyTarget(null); setCopyChecked([]); setConflictDomains([]) }}>取消</Button>
              <Button
                appearance="primary"
                icon={<CopyArrowRightRegular />}
                disabled={copyChecked.length === 0}
                onClick={() => {
                  // 有目标域存在同名模板 → 进入二次确认
                  const conflicted = copyChecked.filter((id) => conflictDomainSet.has(id))
                  if (conflicted.length > 0) {
                    setConflictDomains(conflicted)
                  } else {
                    copyMutation.mutate()
                  }
                }}
              >
                确认复制
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 二次确认：同名模板覆盖 / 跳过 Dialog */}
      <Dialog open={conflictDomains.length > 0} onOpenChange={(_, d) => { if (!d.open) { setConflictDomains([]); setCopyTarget(null); setCopyChecked([]) } }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="复制确认" onClose={() => { setConflictDomains([]); setCopyTarget(null); setCopyChecked([]) }} />
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Text>
                以下收件域已存在与「{copyTarget?.name}」<b>同名</b>的模板，请统一确认对这些域的处理方式：
              </Text>
              <div className={styles.conflictList}>
                {conflictDomains.map((id) => {
                  const d = domains.find((x) => x.id === id)
                  return (
                    <div key={id} className={styles.conflictRow}>
                      <div className={styles.conflictText}>
                        <div className={styles.conflictName}>{d?.name ?? `域 #${id}`}</div>
                        <div className={styles.conflictSub}>存在同名模板「{copyTarget?.name}」</div>
                      </div>
                    </div>
                  )
                })}
              </div>
              <Field
                label={<InfoLabel info="选择「覆盖」会用当前模板内容替换目标域同名模板；选择「跳过」则保留目标域原有模板。">处理方式</InfoLabel>}
              >
                <Select
                  value={conflictAction}
                  onChange={(_, e) => setConflictAction(e.value as 'overwrite' | 'skip')}
                >
                  <option value="overwrite">覆盖（用当前模板替换目标域同名模板）</option>
                  <option value="skip">跳过（保留目标域原有模板）</option>
                </Select>
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="subtle" onClick={() => { setConflictDomains([]); setCopyTarget(null); setCopyChecked([]) }}>取消</Button>
              <Button
                appearance="primary"
                icon={<CopyArrowRightRegular />}
                disabled={copyMutation.isPending}
                onClick={() => copyMutation.mutate()}
              >
                {copyMutation.isPending ? '复制中…' : '确认'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 新建收件域 */}
      <Dialog open={createOpen} onOpenChange={(_, d) => { if (!d.open) setCreateOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="新建收件域" onClose={() => setCreateOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field className={styles.formGridItem} label={<InfoLabel info="收件域 / 投递域的完整域名，例如 example.com。创建后即可在该收件域下管理邮件模板。">域名</InfoLabel>} required>
                  <Input value={domainForm.name} onChange={(_, d) => setDomainForm((f) => ({ ...f, name: d.value }))} placeholder="例如 example.com" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="该域的用途说明，仅作管理备注。">描述</InfoLabel>}>
                  <Input value={domainForm.description} onChange={(_, d) => setDomainForm((f) => ({ ...f, description: d.value }))} placeholder="可选" />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleCreate} disabled={createMutation.isPending}>
                {createMutation.isPending ? '创建中…' : '创建'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 编辑收件域 */}
      <Dialog open={!!editTarget} onOpenChange={(_, d) => { if (!d.open) setEditTarget(null) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="编辑收件域" onClose={() => setEditTarget(null)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field className={styles.formGridItem} label={<InfoLabel info="收件域 / 投递域的完整域名，例如 example.com。">域名</InfoLabel>} required>
                  <Input value={editForm.name} onChange={(_, d) => setEditForm((f) => ({ ...f, name: d.value }))} placeholder="例如 example.com" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="该域的用途说明，仅作管理备注。">描述</InfoLabel>}>
                  <Input value={editForm.description} onChange={(_, d) => setEditForm((f) => ({ ...f, description: d.value }))} placeholder="可选" />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => editMutation.mutate()} disabled={editMutation.isPending}>
                {editMutation.isPending ? '保存中…' : '保存'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除收件域 */}
      <Dialog open={!!deleteTarget} onOpenChange={(_, d) => { if (!d.open) setDeleteTarget(null) }} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogCloseTitle title="删除收件域" onClose={() => setDeleteTarget(null)} />
            <DialogContent>
              <Text>确定要删除收件域 <b>{deleteTarget?.name}</b> 吗？该收件域下的邮件模板将一并影响，此操作不可恢复。</Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="subtle" onClick={() => setDeleteTarget(null)}>取消</Button>
              <Button appearance="primary" icon={<DeleteRegular />} onClick={() => deleteTarget && deleteDomainMutation.mutate(deleteTarget)} disabled={deleteDomainMutation.isPending}>
                {deleteDomainMutation.isPending ? '删除中…' : '删除'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}